import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowRight,
  Sparkles,
  Database,
  CheckCircle,
  Building,
  Mail,
  Phone,
  Compass,
  User,
  BadgeCheck,
  Globe,
  TrendingUp,
} from "lucide-react";

import Seo from "../../Components/SEO/Seo";
import Container from "../../Components/Container/Container";
import Button from "../../Components/Button/Button";
import {
  HERO_HIGHLIGHTS,
  GUARANTEED_BENEFITS,
  PROMISED_BENEFITS_LIST,
  SAMPLE_DATA_POINTS,
} from "../../Data/b2bInsightsData";
import "../../Style/services/b2b-insights.css";

// Map each data point to its corresponding visual icon
const DATA_POINT_ICONS = {
  "Contact Name": User,
  "Job Title": BadgeCheck,
  "Allowance Number": Database,
  "Direct Business Email": Mail,
  "Website URL": Globe,
  "HQ & Branch Location": Building,
  "Consumer Behavior Signals": Compass,
  "Buyer Intent Classification": TrendingUp,
};

const cardHover = {
  initial: { y: 0, opacity: 0.95 },
  hover: { y: -8, opacity: 1, transition: { duration: 0.25, ease: "easeOut" } },
};

export default function B2BContactInsights() {
  const [activeBenefitIndex, setActiveBenefitIndex] = useState(0);

  const handleContactAction = () => {
    window.history.pushState({}, "", "/contact");
    window.dispatchEvent(new Event("popstate"));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const activeBenefit = GUARANTEED_BENEFITS[activeBenefitIndex];
  const ActiveIcon = activeBenefit.icon;

  return (
    <div className="b2b-insights-page">
      <Seo
        title="Expanded B2B Contact Information for Global Markets | Conectify Tech"
        description="Identify active prospects with real-time B2B contact information, verified accuracy guarantees, and perpetual use databases."
      />

      {/* Hero Section */}
      <section className="b2b-hero">
        <Container className="b2b-hero__container">
          <motion.div
            className="b2b-hero__content"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55 }}
          >
            <div className="section-eyebrow">
              <Sparkles size={14} />
              <span>Real-Time B2B Intelligence</span>
            </div>

            <h1 className="b2b-hero__title">
              Expanded B2B Contact Information for{" "}
              <span className="text-gradient">Global Markets</span>
            </h1>

            <p className="b2b-hero__lead">
              Identify active prospects with the best B2B contact provider, and reach potential
              customers the smart way with an intent-based B2B lead database. Generate massive
              revenue and build long-term relationships with the prospects most likely to convert.
            </p>

            <div className="b2b-hero__actions">
              <Button icon={<ArrowRight size={18} />} onClick={handleContactAction}>
                Get Access to Leads
              </Button>
              <Button variant="secondary" onClick={handleContactAction}>
                Consult a Data Specialist
              </Button>
            </div>
          </motion.div>

          {/* Interactive Floating Telemetry Box */}
          <motion.div
            className="b2b-hero__card-wrap"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.55, delay: 0.15 }}
          >
            <div className="b2b-floating-card">
              <div className="b2b-floating-card__top">
                <span className="live-pulse"></span>
                <span>Real-Time B2B Contact Information</span>
              </div>
              <h3 className="b2b-floating-card__headline">Change Your Definition of Success</h3>

              <div className="b2b-telemetry-list">
                {HERO_HIGHLIGHTS.map((item) => {
                  const Icon = item.icon;
                  return (
                    <motion.div
                      key={item.title}
                      className="b2b-telemetry-row"
                      variants={cardHover}
                      initial="initial"
                      whileHover="hover"
                    >
                      <div className="b2b-telemetry-row__icon">
                        <Icon size={18} />
                      </div>
                      <div className="b2b-telemetry-row__text">
                        <h4>{item.title}</h4>
                        <p>{item.subtitle}</p>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </div>
          </motion.div>
        </Container>
      </section>

      {/* Main Implementation Context */}
      <section className="b2b-implementation">
        <Container>
          <div className="b2b-implementation__card">
            <div className="b2b-implementation__grid">
              <div className="b2b-implementation__info">
                <div className="section-eyebrow">Intel 360 Degrees</div>
                <h2>Implement B2B Contact Information in Real-Time with Conectify Tech</h2>
                <p>
                  Knowledge-driven companies perform better in the market and also meet the needs of
                  their ideal target group. Contact information, consumer behavior, and buyer intent
                  help companies make informed decisions with a primary focus on the customer.
                </p>
                <p>
                  The B2B contact details we provide ensure that you have access to Client Insights
                  with guaranteed response rates as the data is verified by human intelligence. This
                  includes information such as contact name, title, allowance number, email address,
                  website address, location, etc. Quality Contact Insights provides Intel 360 degrees
                  in business and helps you make well-versed results that conserve possessions.
                  Relevant data in the right place at the right time is critical to the endurance of a
                  company.
                </p>
              </div>

              {/* Verified Data Field Badges with Specific Icons */}
              <div className="b2b-fields-panel">
                <div className="b2b-fields-panel__title">
                  <Database size={18} className="text-primary" />
                  <span>360° Data Points Captured</span>
                </div>
                <div className="b2b-chips-grid">
                  {SAMPLE_DATA_POINTS.map((field) => {
                    const FieldIcon = DATA_POINT_ICONS[field] || Phone;
                    return (
                      <div key={field} className="b2b-field-chip">
                        <FieldIcon size={16} className="text-primary" />
                        <span>{field}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </Container>
      </section>

      {/* Guaranteed Benefits Interactive Showcase */}
      <section className="b2b-guarantee">
        <Container>
          <div className="section-head text-center">
            <span className="section-eyebrow">Service Level Assurance</span>
            <h2 className="section-title">Guaranteed Benefits of our Potential Contact Database</h2>
            <p className="section-desc">
              We stand firmly behind our multi-tiered verification protocols and provide immediate
              remediations.
            </p>
          </div>

          <div className="b2b-tabs-nav">
            {GUARANTEED_BENEFITS.map((b, idx) => (
              <button
                key={b.id}
                type="button"
                className={`b2b-tab-btn ${activeBenefitIndex === idx ? "is-active" : ""}`}
                onClick={() => setActiveBenefitIndex(idx)}
              >
                <span>{b.title}</span>
              </button>
            ))}
          </div>

          <AnimatePresence mode="wait">
            <motion.div
              key={activeBenefit.id}
              className="b2b-guarantee-showcase"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -16 }}
              transition={{ duration: 0.3 }}
            >
              <div className="b2b-guarantee-showcase__content">
                <span className="b2b-guarantee-badge">{activeBenefit.badge}</span>
                <h3>{activeBenefit.title}</h3>
                <p>{activeBenefit.description}</p>

                <div className="b2b-chip-row">
                  {activeBenefit.chips.map((chip) => (
                    <span key={chip} className="b2b-pill">
                      <CheckCircle size={14} />
                      {chip}
                    </span>
                  ))}
                </div>
              </div>

              <div className="b2b-guarantee-showcase__stat">
                <div className="stat-icon-wrap">
                  <ActiveIcon size={38} />
                </div>
                <div className="stat-number">{activeBenefit.stat}</div>
                <div className="stat-caption">{activeBenefit.statLabel}</div>
              </div>
            </motion.div>
          </AnimatePresence>
        </Container>
      </section>

      {/* Promised Benefits Grid */}
      <section className="b2b-promised">
        <Container>
          <div className="section-head text-center">
            <span className="section-eyebrow">Enterprise Advantage</span>
            <h2 className="section-title">Other Promised Benefits</h2>
            <p className="section-desc">
              Long-term assets engineered for repeatable outbound prospecting and durable ROI.
            </p>
          </div>

          <div className="b2b-promised__grid">
            {PROMISED_BENEFITS_LIST.map((item) => {
              const Icon = item.icon;
              return (
                <motion.div
                  key={item.title}
                  className="b2b-promised-card"
                  variants={cardHover}
                  initial="initial"
                  whileHover="hover"
                >
                  <div className="b2b-promised-card__icon">
                    <Icon size={22} />
                  </div>
                  <h4 className="b2b-promised-card__title">{item.title}</h4>
                  <p className="b2b-promised-card__desc">{item.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </Container>
      </section>

      {/* Call to Action */}
      <section className="b2b-cta">
        <Container>
          <div className="b2b-cta__box">
            <h2>Accelerate Your Pipeline with Precision Contacts</h2>
            <p>
              Connect with decision-makers directly and fuel revenue growth using verified contact
              intelligence tailored to your sector.
            </p>
            <Button icon={<ArrowRight size={18} />} onClick={handleContactAction}>
              Acquire Your Custom List
            </Button>
          </div>
        </Container>
      </section>
    </div>
  );
}